import { removeSettedAttribute, setAttribute } from '../../../dom/attribute';

/**
 * O Focus Trap é uma técnica que evita que o usuário navegue para fora de um
 * certo conteúdo. Para celular com leitores de tela, consiste em deixar tudo
 * fora do elemento escolhido com aria-hidden e assim o leitor de telas não sair
 * do conteúdo especificado.
 *
 * @param element Elemento HTML de onde o foco do leitor de telas não deve sair.
 * @param add Define se vai aplicar ou remover o Focus Trap.
 * @param exceptionList Lista de elementos a serem ignorados.
 *
 * @category A11y
 *
 * @example
 * ```ts
 * import { toggleMobileFocusTrap } from '@ids/tools';
 *
 * const modal = document.querySelector('dialog');
 *
 * toggleMobileFocusTrap(modal);
 * toggleMobileFocusTrap(modal, false);
 * ```
 */
export const toggleMobileFocusTrap = (
  element = document.querySelector('.cdk-overlay-container') as HTMLElement,
  add = true,
  exceptionList = Array.from(document.querySelectorAll('.cdk-live-announcer-element')),
): void => {
  // o focustrap com aria-hidden não é necessário para desktop e causa efeitos
  // colaterais no NVDA na leitura do botão de origem quando fecha o modal.
  if (!element) {
    return;
  }

  const exec = (currentElement: HTMLElement) => {
    const siblingEls = Array.from(currentElement.parentElement?.children ?? []);

    const elementsToHide = siblingEls.filter(
      (siblingEl) =>
        !siblingEl.isEqualNode(currentElement) &&
        !exceptionList.some((el) => el.isEqualNode(siblingEl)) &&
        siblingEl.tagName !== 'SCRIPT' &&
        siblingEl.tagName !== 'STYLE' &&
        siblingEl.tagName !== 'LINK' &&
        siblingEl.tagName !== 'HEAD',
    );

    elementsToHide.forEach((elementToHide) => {
      if (add) {
        setAttribute(elementToHide as HTMLElement, 'aria-hidden', 'true');

        return;
      }

      removeSettedAttribute(elementToHide as HTMLElement, 'aria-hidden');
    });

    if (currentElement.parentElement) {
      exec(currentElement.parentElement);
    }
  };

  exec(element);
};
